import { BCAVisibleSignatureOptions } from "@bcasmart/bca-client-signature/BCAQSCDTypes";
import { DocumentSignaturePosition } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $isuuid, $ok } from "foundation-ts/commons";
import { Nullable, url, UUID } from "foundation-ts/types";

export type InitViewerStyles = {
    header?: Nullable<{ background: string, text?: Nullable<{ color: string, size?: Nullable<string>, family?: Nullable<string> }> }>,
    footer?: Nullable<{ background: string, text?: Nullable<{ color: string, size?: Nullable<string>, family?: Nullable<string> }> }>,
    sidebar?: Nullable<{ background: string, text?: Nullable<{ color: string, size?: Nullable<string>, family?: Nullable<string> }> }>,
    checkbox?: Nullable<{ text: { color: string, size?: Nullable<string>, family?: Nullable<string> }}>,
    previousButton?: Nullable<{ background: string, text?: Nullable<{ color: string, size?: Nullable<string>, family?: Nullable<string> }>, hover?: Nullable<{ background: string }> }>,
    nextButton?: Nullable<{ background: string, text?: Nullable<{ color: string, size?: Nullable<string>, family?: Nullable<string> }>, hover?: Nullable<{ background: string }> }>,
    signButton?: Nullable<{ background: string, text?: Nullable<{ color: string, size?: Nullable<string>, family?: Nullable<string> }>, hover?: Nullable<{ background: string }> }>,
    refuseButton?: Nullable<{ background: string, text?: Nullable<{ color: string, size?: Nullable<string>, family?: Nullable<string> }>, hover?: Nullable<{ background: string }> }>
};

export type SignerInfos = {
    name: string,
    id: string,
    title?: Nullable<string>,
    email?: Nullable<string>,
    position?: DocumentSignaturePosition
};

export type LoadingFailedType = 'ENCRYPTED' | 'READONLY' | 'OTHER';

export interface RemainingSigners {
    docId: UUID,
    signers: SignerInfos[]
}


const MESSAGE_TYPE_VALUES = ['INIT_VIEWER', 'ONE_DOCUMENT_NAME', 'MULTIPLE_DOCUMENT_NAMES', 'AVAILABLE_SIGNERS', 'PLACED_SIGNERS', 'SHOW_SIGNER_POSITION', 'SAVE_SIGNER_POSITION', 'REMOVE_SIGNER_POSITION', 'DOC_URL_DATA_REQ', 'DOC_URL_DATA_RESP', 'DOWNLOADING_REQ', 'PRINTING_REQ', 'SIGNING_REQ', 'REFUSING_REQ', 'LOADING_FAILED'] as const;
export type MessageType = typeof MESSAGE_TYPE_VALUES[number];

type PostMessage = { type: MessageType, token: UUID };
export type InitViewerMessage = PostMessage & { payload: {
    appName: string,
    styles: InitViewerStyles,
    debug?: Nullable<boolean>,
    placingSignersView?: Nullable<boolean>,
    signingView?: Nullable<boolean>,
    usingSelector?: Nullable<boolean>,
    autoExternalSavePosition?: Nullable<boolean>,
    showRefuseButton?: Nullable<boolean>,
    callbackNames?: {
        urldata?: MessageType,
        signing?: MessageType,
        refuse?: MessageType,
        printing?: MessageType,
        download?: MessageType
    }
    cguURL?: Nullable<url>,
    privacyURL?: Nullable<url>,
}};
export type DocumentNamesMessage = PostMessage & { docs: Array<{docName: string, docId: UUID}> };
export type DocumentURLDataRespMessage = PostMessage & { payload: { 
    docId: UUID,
    urlData?: Nullable<string>,
    error?: Nullable<string>,
}};
export type DocumentURLDataReqMessage = PostMessage & { docId: UUID };
export type AvailableSignersMessage = PostMessage & { payload: { 
    signers: SignerInfos[],
    forceUpdateView?: Nullable<boolean>
}};
export type PlacedSignersMessage = PostMessage & { payload: { 
    docId: UUID,
    signers: DocumentSignaturePosition[],
    forceUpdateView?: Nullable<boolean>
}};
export type SaveSignerPositionMessage = PostMessage & { payload: { 
    docId: UUID,
    docPosition: DocumentSignaturePosition
}};
export type RemoveSignerPositionMessage = PostMessage & { payload: {
    docId: UUID,
    signatureId: UUID
}};
export type PrintDocMessage = PostMessage & { urlData: string };
export type DownloadDocMessage = PostMessage & { payload: {
    filename: string,
    urlData: string
}};
export type ShowSignerPositionMessage = PostMessage & { positions: DocumentSignaturePosition[], remainingSigners?: RemainingSigners[], visibleOptions?: Nullable<BCAVisibleSignatureOptions> };
export type LoadingFailedMessage = PostMessage & { error: LoadingFailedType };

function isPostMessage(data: unknown, token: UUID): boolean {
    return typeof data === 'object' && $ok(data) && (data as any).token === token && MESSAGE_TYPE_VALUES.includes((data as any).type);
}

export function isDocumentURLDataReqMessage(data : unknown, token: UUID): data is DocumentURLDataReqMessage {
    return isPostMessage(data, token) && (data as any).type === 'DOC_URL_DATA_REQ' && $isuuid((data as any).docId);
}

export function isSaveSignerPositionMessage(data: unknown, token: UUID): data is SaveSignerPositionMessage {
    if (!isPostMessage(data, token) || (data as any).type !== 'SAVE_SIGNER_POSITION' || typeof (data as any).payload !== 'object') {
        return false;
    }

    return ($isuuid((data as any).payload.docId) &&
            typeof (data as any).payload.docPosition === 'object' &&
            $isuuid((data as any).payload.docPosition.identifier) &&
            typeof (data as any).payload.docPosition.page === 'number' &&
            typeof (data as any).payload.docPosition.top === 'number' &&
            typeof (data as any).payload.docPosition.left === 'number' &&
            typeof (data as any).payload.docPosition.width === 'number' &&
            typeof (data as any).payload.docPosition.height === 'number'
    );
}

export function isRemoveSignerPositionMessage(data: unknown, token: UUID): data is RemoveSignerPositionMessage {
    if (!isPostMessage(data, token) || (data as any).type !== 'REMOVE_SIGNER_POSITION' || typeof (data as any).payload !== 'object') {
        return false;
    }

    return $isuuid((data as any).payload.docId) && $isuuid((data as any).payload.signatureId);
}

export function isPrintDocMessage(data: unknown, token: UUID): data is PrintDocMessage {
    return isPostMessage(data, token) && (data as any).type === 'PRINTING_REQ' && typeof (data as any).urlData === 'string';
}

export function isDownloadDocMessage(data: unknown, token: UUID): data is DownloadDocMessage {
    if (!isPostMessage(data, token) || (data as any).type !== 'DOWNLOADING_REQ' || typeof (data as any).payload !== 'object') {
        return false;
    }

    return typeof (data as any).payload.filename === 'string' && typeof (data as any).payload.urlData === 'string';
}

export function isSignDocMessage(data: unknown, token: UUID): boolean {
    return isPostMessage(data, token) && (data as any).type === 'SIGNING_REQ';
}

export function isRefuseDocMessage(data: unknown, token: UUID): boolean {
    return isPostMessage(data, token) && (data as any).type === 'REFUSING_REQ';
}

export function isLoadingFailedMessage(data: unknown, token: UUID): boolean {
    return isPostMessage(data, token) && (data as any).type === 'LOADING_FAILED';
}